import { cn } from "@design-system/utils";
import * as SelectPrimitive from "@radix-ui/react-select";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import * as React from "react";

function Select({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return (
    <SelectPrimitive.Group
      data-slot="select-group"
      className={cn("w-full", className)}
      {...props}
    />
  );
}

function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
  className,
  children,
  onPointerDown,
  onClick,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger>) {
  const hadPointerDownRef = React.useRef(false);

  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={cn(
        "border-shape-neutral-outlined-border-emphasised bg-shape-neutral-outlined-background text-shape-neutral-outlined-foreground-default data-[placeholder]:text-shape-neutral-outlined-foreground-muted focus-visible:ring-ring-default focus:ring-ring-default aria-invalid:border-shape-critical-outlined-border-emphasised flex h-9 cursor-pointer items-center justify-between gap-2 self-stretch rounded-lg border px-3 py-2 text-left text-sm leading-normal font-normal whitespace-nowrap transition-[color,box-shadow] outline-none focus:ring-[3px] focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex-1",
        className
      )}
      onPointerDown={(event) => {
        hadPointerDownRef.current = true;
        onPointerDown?.(event);
      }}
      onClick={(event) => {
        // When a <label htmlFor> is clicked, the browser dispatches a click on
        // the associated element but NOT a pointerdown. Per HTML spec, clicking
        // a label for a native <select> focuses it without opening the dropdown.
        // Match that behavior: focus the trigger and prevent Radix from opening.
        // Keyboard activation (Enter/Space) also has no pointerdown, but Radix
        // already opens on keydown so preventing the click is safe.
        if (!hadPointerDownRef.current) {
          event.currentTarget.focus();
          event.preventDefault();
        }
        hadPointerDownRef.current = false;
        onClick?.(event);
      }}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="text-shape-neutral-outlined-foreground-default size-5 shrink-0" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          "bg-background border-border-default data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-120 flex max-h-(--radix-select-content-available-height) max-h-[384px] min-w-[8rem] origin-(--radix-select-content-transform-origin) flex-col items-center self-stretch overflow-x-hidden overflow-y-auto rounded-lg border p-1 shadow-md",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "flex flex-col items-start self-stretch p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1"
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn(
        "text-foreground-neutral-muted px-2 py-1.5 text-xs leading-normal font-medium",
        className
      )}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "text-shape-neutral-ghost-foreground-default focus:bg-shape-neutral-ghost-background-hover [&_svg]:text-shape-neutral-ghost-foreground-default relative flex h-8 flex-row items-center gap-2 self-stretch rounded-lg px-2 py-1.5 pr-8 text-sm leading-normal font-normal outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <SelectPrimitive.ItemText asChild>
        <span className="pointer-events-none flex items-center gap-2 overflow-hidden text-ellipsis whitespace-nowrap">
          {children}
        </span>
      </SelectPrimitive.ItemText>
      <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="text-shape-neutral-ghost-foreground-default size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn(
        "bg-shape-neutral-outlined-border-default pointer-events-none -mx-1 my-1 h-px",
        className
      )}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className
      )}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className
      )}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
